"use client";

import Script from "next/script";
import { useSyncExternalStore } from "react";
import { POSTHOG_READY_EVENT } from "@/lib/analytics";
import { isNativeApp, subscribeToNativePlatform } from "@/lib/native/platform";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;

export function buildPostHogBootstrapScript(projectKey: string, native = false): string {
  const encodedKey = JSON.stringify(projectKey);
  const encodedReadyEvent = JSON.stringify(POSTHOG_READY_EVENT);
  const nativePrivacy = native ? "autocapture:false,disable_session_recording:true," : "";
  // Cover the first automatic page view as well as later history changes.
  // /confirm-dates links carry a signed token that can change a window's
  // status, so their query string is removed like a day-out selection.
  const dayOutPrivacy = String.raw`
function redactDayOutUrl(value) {
  if (typeof value !== "string") return value;
  if (/https?(?::|%3a)(?:\/\/|%2f%2f)(?:stream|image)\.mux\.com/i.test(value)) return "[private video address removed]";
  if (!/(?:outing|invitation|sharedOuting|sharedVersion|sharedRoute|trophy)(?:=|%3d|%253d)|plan(?:\/|%2f|%252f)trips(?:[?#]|%3f|%253f)|freestyle(?:[/?#]|%2f|%3f|%253f)|notifications(?:\/|%2f|%252f)open|confirm-dates(?:[?#]|%3f|%253f)/i.test(value)) return value;
  try {
    var url = new URL(value, "https://peakwave.co");
    return value.startsWith("/") ? url.pathname : url.origin + url.pathname;
  } catch (_) { return "[day-out selection removed]"; }
}
function redactDayOutProperties(value) {
  if (typeof value === "string") return redactDayOutUrl(value);
  if (Array.isArray(value)) return value.map(redactDayOutProperties);
  if (value && typeof value === "object") {
    Object.keys(value).forEach(function(key) { value[key] = redactDayOutProperties(value[key]); });
  }
  return value;
}
function isPrivateCompanionRequest(value) {
  if (typeof value !== "string") return false;
  try {
    var path = decodeURIComponent(new URL(value, "https://peakwave.co").pathname);
    if (/^https:\/\/(?:stream|image)\.mux\.com(?:\/|$)/i.test(value)) return true;
    if (/^\/storage\/v1\/(?:upload\/resumable(?:\/|$)|object\/(?:upload\/)?sign\/companion-freestyle-sources(?:\/|$))/.test(path)) return true;
    return /^\/api\/companion\/(?:freestyle|outings|calendar|rota|data|availability|alerts|public-crowns|public-records|stays)(?:\/|$)/.test(path)
      || /^\/api\/admin\/companion-(?:freestyle|operations)\/?$/.test(path)
      || /^\/api\/companion\/(?:(?:together(?:\/destinations)?|free-time-sharing|people|crew-location|location|updates|outing-map|outing-invitations)\/?|feedback\/?|recap\/?|calendar\/import\/?|memories\/[^/]+\/?|outings\/[^/]+\/feedback\/?)$/.test(path)
      || /^\/api\/planner\/[^/]+\/(?:directions|adaptive|guidance)\/?$/.test(path)
      || /^\/api\/native\/widget\/companion\/?$/.test(path);
  } catch (_) { return false; }
}`;
  const dayOutOptions = String.raw`before_send:function(event){
    if(event && event.properties) event.properties=redactDayOutProperties(event.properties);
    return event;
  },session_recording:{
    blockSelector:'.ph-no-capture, [href*="outing="]',
    maskCapturedNetworkRequestFn:function(request){
      if(isPrivateCompanionRequest(request.name)) return null;
      if(request.name) request.name=redactDayOutUrl(request.name);
      return request;
    }
  },`;

  return `if(["peakwave.co","www.peakwave.co"].includes(window.location.hostname)){${dayOutPrivacy}!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init(${encodedKey},{${nativePrivacy}${dayOutOptions}api_host:"https://eu.i.posthog.com",ui_host:"https://eu.posthog.com",defaults:"2026-05-30",capture_pageview:"history_change",person_profiles:"identified_only",loaded:function(){window.dispatchEvent(new CustomEvent(${encodedReadyEvent}))}});}`;
}

export function PostHogScript() {
  const native = useSyncExternalStore(subscribeToNativePlatform, isNativeApp, () => false);
  if (!POSTHOG_KEY) return null;

  return (
    <Script
      id="posthog"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: buildPostHogBootstrapScript(POSTHOG_KEY, native),
      }}
    />
  );
}
